"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
const essentials = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profiles.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profiles.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profiles.webp-wvc-sizes",
  alt: "Precision-cut aluminum profiles stacked in a warehouse",
  category: "STOCK \u00B7 INDUSTRIAL",
  nameStart: "Premium",
  accent: "Aluminum",
  nameEnd: "Profiles",
  reasoning: "High-grade architectural aluminum extrusions engineered for structural integrity. Available in bulk for large-scale commercial projects across the UAE."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware.png-wvc-sizes",
  alt: "Close up of architectural door hardware and fittings",
  category: "HARDWARE \u00B7 PRECISION",
  nameStart: "Architectural",
  accent: "Hardware",
  nameEnd: "Systems",
  reasoning: "Durable, corrosion-resistant fittings designed for modern facades. We supply the essential components that define the longevity of your build."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/global_supply_chain.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/global_supply_chain.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/global_supply_chain.webp-wvc-sizes",
  alt: "Global logistics map showing supply chain routes",
  category: "LOGISTICS \u00B7 GLOBAL",
  nameStart: "Direct",
  accent: "Manufacturing",
  nameEnd: "Partnerships",
  reasoning: "Leveraging direct manufacturing partnerships in China to ensure consistent quality and competitive wholesale pricing for our regional clients."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/warehouse_storage.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/warehouse_storage.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/warehouse_storage.webp-wvc-sizes",
  alt: "Organized warehouse storage facility in Sharjah",
  category: "STORAGE \u00B7 REGIONAL",
  nameStart: "Strategic",
  accent: "Warehouse",
  nameEnd: "Presence",
  reasoning: "Our expansive facilities in Sharjah and Ajman ensure rapid deployment of materials to your project sites in Al Ain and beyond."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/quality_control.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/quality_control.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/quality_control.png-wvc-sizes",
  alt: "Quality control inspection of metal components",
  category: "QUALITY \u00B7 CERTIFIED",
  nameStart: "Engineered",
  accent: "Precision",
  nameEnd: "Standards",
  reasoning: "Every batch undergoes rigorous inspection to meet international building codes, ensuring your construction projects remain compliant and reliable."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/logistics_fleet.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/logistics_fleet.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/logistics_fleet.png-wvc-sizes",
  alt: "Logistics delivery fleet ready for dispatch",
  category: "DELIVERY \u00B7 FAST",
  nameStart: "Reliable",
  accent: "Supply",
  nameEnd: "Flow",
  reasoning: "Optimized logistics management that minimizes downtime. We deliver the materials you need, exactly when your project schedule demands it."
}];
const logisticsMetrics = [{
  label: "LEAD TIME",
  value: "24-48 HRS"
}, {
  label: "BULK CAPACITY",
  value: "500+ TONS"
}, {
  label: "FLEET SIZE",
  value: "15+ UNITS"
}];
export default function Logistics() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="6161" id="warehouse-logistics" className="bg-slate-950 text-foreground py-24 md:py-36">
      <div className="mx-auto max-w-[1320px] px-5 md:px-6 lg:px-10">
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">
          <div className={`lg:col-span-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <span className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Warehouse & Logistics
            </span>
            <h2 className="font-default mt-8 text-primary-foreground" style={{
            fontWeight: 700,
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: 1.2
          }}>
              High-capacity bulk supply <br />
              <span className="text-primary-foreground">
                Trusted by UAE contractors
              </span>
            </h2>
          </div>

          <div className={`lg:col-span-4 lg:self-end transition-all duration-700 ease-out delay-100 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <p className="font-default text-primary-foreground" style={{
            fontSize: "1.125rem",
            fontWeight: 400,
            lineHeight: 1.6,
            maxWidth: "35ch"
          }}>
              Optimizing the regional supply chain through precision logistics and direct manufacturing oversight to ensure zero-downtime delivery for large-scale infrastructure.
            </p>
            
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
              {logisticsMetrics.map((metric, idx) => <div key={idx} data-index={idx} className="flex flex-col">
                  <span className="font-mono text-[10px] tracking-widest text-foreground/50 uppercase">
                    {metric.label}
                  </span>
                  <span className="font-mono text-sm font-bold text-primary mt-1">
                    {metric.value}
                  </span>
                </div>)}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild className="rounded-lg bg-primary hover:bg-primary/90 text-white px-8 py-6 h-auto">
                <Link to="/ConnectWithSalesPage">Request Quote</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-lg border-white/20 hover:bg-white/5 text-white px-8 py-6 h-auto">
                <Link to="/ConnectWithSalesPage">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 md:gap-y-20">
          {essentials.map((item, i) => {
          return <EssentialItem key={i} item={item} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function EssentialItem({
  item,
  index
}: {
  item: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
    category: string;
    nameStart: string;
    accent: string;
    nameEnd: string;
    reasoning: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`group flex flex-col transition-all ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDuration: "600ms",
    transitionDelay: `${index % 3 * 50}ms`,
    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
  }}>
      <div className="relative overflow-hidden aspect-[4/5] bg-slate-900 rounded-lg">
        <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform ease-out ${isVisible ? "scale-100" : "scale-105"} group-hover:scale-[1.03]`} style={{
        transitionDuration: "900ms"
      }} />
      </div>

      <div className="mt-8 font-mono text-primary" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.05em"
    }}>
        {item.category}
      </div>

      <h3 className="font-default mt-4 text-foreground transition-colors duration-200" style={{
      fontWeight: 600,
      fontSize: "1.75rem",
      letterSpacing: "-0.01em",
      lineHeight: 1.3
    }}>
        {item.nameStart}{" "}
        <span className="text-primary">
          {item.accent}
        </span>
        {item.nameEnd ? ` ${item.nameEnd}` : ""}
      </h3>

      <p className="font-default mt-5 text-primary-foreground" style={{
      fontSize: "1rem",
      lineHeight: 1.6,
      maxWidth: "32ch"
    }}>
        {item.reasoning}
      </p>

      <Link to="/MaterialCatalogPage" className="mt-6 inline-flex items-center self-start font-default text-sm font-medium transition-all duration-200 border-b border-primary pb-1" style={{
      color: "var(--primary)"
    }}>
        <span className="transition-transform duration-200 group-hover:translate-x-1">
          View Specifications &rarr;
        </span>
      </Link>
    </div>;
}